import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { SupportSession } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { StatusDot } from '@/shared/ui/status-dot';

import { useEndSupportSession } from '../hooks/use-support-session-mutations';
import { useSupportSessions, type SupportSessionFilter } from '../hooks/use-support-sessions';

export interface SupportSessionsTableProps {
  filter: SupportSessionFilter;
  /** Owners and admins may end a live session. */
  canManage: boolean;
  /** Names for the organization column; omitted on an organization's own page. */
  organizationNames?: ReadonlyMap<string, string>;
  label: string;
}

/** Support sessions newest first, with who opened them, why, and until when. */
export function SupportSessionsTable({
  filter,
  canManage,
  organizationNames,
  label,
}: SupportSessionsTableProps) {
  const { t } = useTranslation('organizations');
  const query = useSupportSessions(filter);
  const end = useEndSupportSession();
  const sessions = query.data?.pages.flatMap((page) => page.items) ?? [];
  const showOrganization = organizationNames !== undefined;
  const columns = showOrganization ? 6 : 5;

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <Table className="text-[13px]" aria-label={label}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 pl-5 text-xs font-medium">
              {t('sessions.columns.status')}
            </TableHead>
            {showOrganization ? (
              <TableHead className="h-10 text-xs font-medium">
                {t('sessions.columns.organization')}
              </TableHead>
            ) : null}
            <TableHead className="h-10 text-xs font-medium">
              {t('sessions.columns.operator')}
            </TableHead>
            <TableHead className="h-10 text-xs font-medium">
              {t('sessions.columns.reason')}
            </TableHead>
            <TableHead className="h-10 text-xs font-medium">{t('sessions.columns.when')}</TableHead>
            <TableHead className="h-10 pr-5 text-right text-xs font-medium">
              <span className="sr-only">{t('sessions.columns.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isPending ? (
            <TableRow>
              <TableCell colSpan={columns} className="px-5">
                <Skeleton className="h-4 w-full" />
              </TableCell>
            </TableRow>
          ) : sessions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns} className="text-muted-foreground h-20 text-center">
                {t('sessions.empty')}
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((session, index) => (
              <SessionRow
                key={session.id}
                first={index === 0}
                session={session}
                organizationName={organizationNames?.get(session.organizationId)}
                showOrganization={showOrganization}
                canEnd={canManage && session.active}
                ending={end.isPending && end.variables === session.id}
                onEnd={() => end.mutate(session.id)}
              />
            ))
          )}
        </TableBody>
      </Table>
      {query.hasNextPage ? (
        <Button
          variant="outline"
          size="sm"
          className="self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {t('sessions.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}

interface SessionRowProps {
  session: SupportSession;
  first: boolean;
  organizationName: string | undefined;
  showOrganization: boolean;
  canEnd: boolean;
  ending: boolean;
  onEnd: () => void;
}

function SessionRow({
  session,
  first,
  organizationName,
  showOrganization,
  canEnd,
  ending,
  onEnd,
}: SessionRowProps) {
  const { t } = useTranslation('organizations');
  const format = useFormat();
  const until = session.endedAt ?? session.expiresAt;
  return (
    <TableRow>
      <TableCell className="py-3 pl-5">
        <StatusDot tone={session.active ? 'success' : 'muted'} pulse={session.active}>
          {session.active
            ? t('sessions.status.active')
            : session.endedAt
              ? t('sessions.status.ended')
              : t('sessions.status.expired')}
        </StatusDot>
      </TableCell>
      {showOrganization ? (
        <TableCell className="py-3">
          <Link
            to="/organizations/$organizationId"
            params={{ organizationId: session.organizationId }}
            className="font-medium hover:underline"
          >
            {organizationName ?? session.organizationId}
          </Link>
        </TableCell>
      ) : null}
      <TableCell className="py-3">
        <span className="flex flex-col">
          <span className="font-medium">{session.platformUser.name}</span>
          <span className="text-muted-foreground">{session.platformUser.email}</span>
        </span>
      </TableCell>
      <TableCell className="max-w-72 py-3 whitespace-normal">{session.reason}</TableCell>
      <TableCell className="text-muted-foreground py-3 whitespace-nowrap">
        <span title={format.utc(session.createdAt)}>{format.dateTime(session.createdAt)}</span>
        <br />
        <span title={format.utc(until)}>
          {session.active
            ? t('sessions.until', { time: format.time(until) })
            : t('sessions.endedAt', { time: format.dateTime(until) })}
        </span>
      </TableCell>
      <TableCell className="py-3 pr-5 text-right">
        {canEnd ? (
          <Button
            variant="outline"
            size="sm"
            disabled={ending}
            onClick={onEnd}
            data-tour={first ? 'platform.support-sessions.end' : undefined}
          >
            {t('sessions.end')}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
